const kPropagationStopped = Symbol("kPropagationStopped");
const kImmediatePropagationStopped = Symbol("kImmediatePropagationStopped");
class Emitter {
  #listeners;
  #listenerOptions;
  #eventsCache;
  #abortControllers;
  constructor() {
    this.#listeners = {};
    this.#listenerOptions = /* @__PURE__ */ new WeakMap();
    this.#eventsCache = /* @__PURE__ */ new WeakMap();
    this.#abortControllers = /* @__PURE__ */ new WeakMap();
  }
  /**
   * Adds a listener for the given event type.
   */
  on(type, listener, options) {
    this.#addListener(type, listener);
    const abortController = this.#createAbortController(type, listener);
    this.#listenerOptions.set(listener, {
      signal: options?.signal ? AbortSignal.any([abortController.signal, options.signal]) : abortController.signal
    });
    return abortController;
  }
  /**
   * Adds a one-time listener for the given event type.
   */
  once(type, listener, options) {
    this.#addListener(type, listener);
    const abortController = this.#createAbortController(type, listener);
    this.#listenerOptions.set(listener, {
      once: true,
      signal: options?.signal ? AbortSignal.any([abortController.signal, options.signal]) : abortController.signal
    });
    return abortController;
  }
  /**
   * Prepends a listener for the given event type.
   */
  earlyOn(type, listener, options) {
    if (!this.#listeners[type]) {
      this.#listeners[type] = [];
    }
    this.#listeners[type].unshift(listener);
    const abortController = this.#createAbortController(type, listener);
    this.#listenerOptions.set(listener, {
      signal: options?.signal ? AbortSignal.any([abortController.signal, options.signal]) : abortController.signal
    });
    return abortController;
  }
  /**
   * Prepends a one-time listener for the given event type.
   */
  earlyOnce(type, listener, options) {
    this.earlyOn(type, listener);
    const abortController = this.#createAbortController(type, listener);
    this.#listenerOptions.set(listener, {
      once: true,
      signal: options?.signal ? AbortSignal.any([abortController.signal, options.signal]) : abortController.signal
    });
    return abortController;
  }
  emit(...args) {
    const [eventOrType, data] = args;
    const isEvent = eventOrType instanceof Event;
    const type = isEvent ? eventOrType.type : eventOrType;
    if (!this.#listeners[type] || this.#listeners[type].length === 0) {
      return false;
    }
    const event = isEvent ? eventOrType : this.createEvent.call(this, type, data);
    for (const listener of this.#listeners[type]) {
      if (event[kPropagationStopped] != null && event[kPropagationStopped] !== this) {
        return false;
      }
      if (event[kImmediatePropagationStopped]) {
        break;
      }
      if (this.#listenerOptions.get(listener).signal.aborted) {
        continue;
      }
      this.#callListener(listener, event);
    }
    this.#eventsCache.delete([type, data]);
    return true;
  }
  async emitAsPromise(...args) {
    const [eventOrType, data] = args;
    const isEvent = eventOrType instanceof Event;
    const type = isEvent ? eventOrType.type : eventOrType;
    if (!this.#listeners[type] || this.#listeners[type].length === 0) {
      return [];
    }
    const event = isEvent ? eventOrType : this.createEvent.call(this, type, data);
    const pendingListeners = [];
    for (const listener of this.#listeners[type]) {
      if (event[kPropagationStopped] != null && event[kPropagationStopped] !== this) {
        return [];
      }
      if (event[kImmediatePropagationStopped]) {
        break;
      }
      if (this.#listenerOptions.get(listener).signal.aborted) {
        continue;
      }
      pendingListeners.push(
        // Awaiting individual listeners guarantees their call order.
        await Promise.resolve(this.#callListener(listener, event))
      );
    }
    this.#eventsCache.delete([type, data]);
    return Promise.allSettled(pendingListeners).then((results) => {
      return results.map(
        (result) => result.status === "fulfilled" ? result.value : result.reason
      );
    });
  }
  *emitAsGenerator(...args) {
    const [eventOrType, data] = args;
    const isEvent = eventOrType instanceof Event;
    const type = isEvent ? eventOrType.type : eventOrType;
    if (!this.#listeners[type] || this.#listeners[type].length === 0) {
      return;
    }
    const event = isEvent ? eventOrType : this.createEvent.call(this, type, data);
    for (const listener of this.#listeners[type]) {
      if (event[kPropagationStopped] != null && event[kPropagationStopped] !== this) {
        return;
      }
      if (event[kImmediatePropagationStopped]) {
        break;
      }
      if (this.#listenerOptions.get(listener).signal.aborted) {
        continue;
      }
      yield this.#callListener(listener, event);
    }
    this.#eventsCache.delete([type, data]);
  }
  /**
   * Removes the given listener.
   */
  removeListener(type, listener) {
    if (!this.#listeners[type]) {
      return;
    }
    const nextListeners = [];
    for (const existingListener of this.#listeners[type]) {
      if (existingListener === listener) {
        this.#listenerOptions.delete(existingListener);
        this.#abortControllers.delete(existingListener);
        this.#eventsCache.delete([type, existingListener]);
        continue;
      }
      nextListeners.push(existingListener);
    }
    this.#listeners[type] = nextListeners;
  }
  /**
   * Removes all listeners for the given event type.
   * If no event type is provided, removes all existing listeners.
   */
  removeAllListeners(type) {
    if (type == null) {
      this.#listeners = {};
      this.#listenerOptions = /* @__PURE__ */ new WeakMap();
      this.#abortControllers = /* @__PURE__ */ new WeakMap();
      this.#eventsCache = /* @__PURE__ */ new WeakMap();
      return;
    }
    this.#listeners[type] = [];
  }
  /**
   * Returns the list of listeners for the given event type.
   * If no even type is provided, returns all listeners.
   */
  listeners(type) {
    if (type == null) {
      return Object.values(this.#listeners).flat();
    }
    return this.#listeners[type];
  }
  /**
   * Returns the number of listeners for the given event type.
   * If no even type is provided, returns the total number of listeners.
   */
  listenerCount(type) {
    return this.listeners(type).length;
  }
  #addListener(type, listener) {
    if (!this.#listeners[type]) {
      this.#listeners[type] = [];
    }
    this.#listeners[type].push(listener);
  }
  createEvent(...args) {
    const [type, data] = args;
    const cachedEvent = this.#eventsCache.get([type, data]);
    if (cachedEvent) {
      return cachedEvent;
    }
    let event = data == null ? new Event(type, { cancelable: true }) : new MessageEvent(type, { data, cancelable: true });
    Object.defineProperties(event, {
      defaultPrevented: {
        enumerable: false,
        writable: true,
        value: false
      },
      preventDefault: {
        enumerable: false,
        value: new Proxy(event.preventDefault, {
          apply: (target, thisArg, argArray) => {
            Reflect.set(event, "defaultPrevented", true);
            return Reflect.apply(target, thisArg, argArray);
          }
        })
      },
      stopPropagation: {
        enumerable: false,
        value: new Proxy(event.stopPropagation, {
          apply: (target, thisArg, argArray) => {
            event[kPropagationStopped] = this;
            return Reflect.apply(target, thisArg, argArray);
          }
        })
      },
      stopImmediatePropagation: {
        enumerable: false,
        value: new Proxy(event.stopImmediatePropagation, {
          apply(target, thisArg, argArray) {
            event[kImmediatePropagationStopped] = true;
            return Reflect.apply(target, thisArg, argArray);
          }
        })
      }
    });
    this.#eventsCache.set([type, data], event);
    return event;
  }
  #callListener(listener, event) {
    const listenerResult = listener.call(this, event);
    if (this.#listenerOptions.get(listener)?.once) {
      this.removeListener(event.type, listener);
    }
    return listenerResult;
  }
  #createAbortController(type, listener) {
    const abortController = new AbortController();
    abortController.signal.addEventListener("abort", () => {
      this.removeListener(type, listener);
    });
    this.#abortControllers.set(listener, abortController);
    return abortController;
  }
}
export {
  Emitter
};
//# sourceMappingURL=index.js.map